import React, { ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import parse from 'html-react-parser';
import { styled } from '@linaria/react';
import { ArticleWrapper } from '@rambler-help/components';
import {
  RouteNames,
  RouteParameters,
  EArticleType,
  EPageType,
  fontMontserrat,
  fontColorDefault,
  WrapperStyles,
  RightColumnStyles,
  styleLink
} from '@rambler-help/shared';

import { ScrollUp } from '@components/ScrollUp';
import { Back } from '@components/Back';
import { SITE_TITLE } from '@constants/meta';
import { parserOptions } from '@helpers/parserOptions';
import { useRootStore } from '@stores/index';

const ArticlePageStyled = styled.div`
  ${WrapperStyles}
  h1 {
    margin: 30px 0 20px;
    ${fontMontserrat}
    ${fontColorDefault}
    font-size: 26px;
    line-height: 30px;
    font-weight: 800;
  }
`;
const Content = styled.div`
  ${RightColumnStyles}
`;

export const Article = observer((): ReactElement => {
  const { projects, sections, articles } = useRootStore();
  let ogParam;
  let title = '';
  let content = '';

  if (sections.activeSection && !sections.activeSubsection) {
    ogParam = `sectionId=${sections.activeSection?.id}`;
    title = sections.activeSection?.title || '';
    content = sections.activeSection?.body || '';
  } else {
    ogParam = `articleId=${articles.article?.id}`;
    title = articles?.article?.title || '';
    content = articles?.article?.body || '';
  }

  return (
    <>
      <Helmet>
        <title>{`${title} — ${SITE_TITLE}`}</title>
        <meta
          property="og:image"
          content={`https://img-renderer.rambler.ru/api/generate/help?${ogParam}`}
        />
      </Helmet>
      <ArticlePageStyled>
        <Content>
          <Back to="/">Все сервисы Рамблера</Back>
          <h1>{title}</h1>
          {articles.article?.type === EArticleType.Legal && (
            <ExpiredDocument
              started={articles.article.introduceTime}
              expired={articles.article.expireTime}
              nextId={articles.article.nextId}
              projectSlug={projects.project?.slug || ''}
            />
          )}
          <ArticleWrapper>
            <div id="content">
              {parse(content, parserOptions)}
            </div>
          </ArticleWrapper>
          <ScrollUp />
        </Content>
      </ArticlePageStyled>
      {/* <Feedback /> */}
    </>
  );
});

const ExpiredDocumentStyledComponent = styled.div`
  padding-bottom: 10px;
  font-size: 12px;
  line-height: 20px;
  color: #8D96B2;
  p {
    margin-bottom: 10px;
  }
  ${styleLink}
`;
const Expired = styled.div`
  position: relative;
  display: inline-block;
  height: 30px;
  margin-right: 10px;
  padding: 0 10px 0 40px;
  ${fontColorDefault}
  line-height: 30px;
  background-color: #ffeeed;
  border-radius: 5px;
  background-image: url('./images/attention.svg');
  background-repeat: no-repeat;
  background-position: 10px 5px;
`;
const Started = styled.div`
  display: inline-block;
  height: 30px;
  line-height: 30px;
`;

interface IExpiredDocumentProps {
  started?: Date | null;
  expired?: Date | null;
  nextId?: number;
  projectSlug: string;
}

const months = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля',
    'августа', 'сентября', 'октября', 'ноября', 'декабря'
  ];

const timeToString = (time: Date) => {
  return (`${time.getDate()} ${months[time.getMonth()]} ${time.getFullYear()}`);
};

const ExpiredDocument = observer((
  { started, expired, nextId, projectSlug }: IExpiredDocumentProps
): ReactElement => {
  const { layout } = useRootStore();
  const currentTime = new Date();
  const userTimezoneOffset = currentTime.getTimezoneOffset() * 60000;
  let startedString;
  let expiredString;

  if (started) {
    const time = (
      typeof started === 'object'
        ? (started as Date)
        : new Date((new Date(started as string).getTime() + userTimezoneOffset))
    );

    if (time.getTime() < currentTime.getTime()) {
      startedString = timeToString(time);
    }
  }

  if (expired) {
    const time = (
      typeof expired === 'object'
        ? (expired as Date)
        : new Date((new Date(expired as string).getTime() + userTimezoneOffset))
    );

    if (time.getTime() < currentTime.getTime()) {
      expiredString = timeToString(time);
    }
  }

  return (
    <ExpiredDocumentStyledComponent>
      {(expiredString || startedString) && (
        <p>
          {expiredString && (
            <Expired>
              Документ утратил свою силу {expiredString}
            </Expired>
          )}
          {startedString && (
            <Started>
              {expiredString ? (
                <>Вступил в силу {startedString}</>
              ) : (
                <>Документ вступил в силу {startedString}</>
              )}
            </Started>
          )}
        </p>
      )}
      {nextId !== undefined && (
        <p>
          Перейти в <a
            href={(
              layout?.pageType === EPageType.LegalSeo
                ? RouteNames.USER_LEGAL_ARTICLE_PAGE
                : RouteNames.USER_COMMON_ARTICLE_PAGE
            ).replace(
              RouteParameters.ARTICLE_ID,
              nextId.toString()
            ).replace(
              RouteParameters.PROJECT_SLUG,
              projectSlug
            )}
          >действующий документ</a>
        </p>
      )}
    </ExpiredDocumentStyledComponent>
  );
});
